import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatMoney } from '@/components/admin/licence-status-chip';
import { PageHeader } from '@/components/page-header';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { adminApi, adminKeys, adminPlansQuery, type AdminPlan } from '@/lib/api/admin';
import { messageForError } from '@/lib/api-client';

function PlanEditor({ plan, onClose }: { plan: AdminPlan; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(plan.name);
  const [active, setActive] = useState(plan.active);
  const [isPublic, setIsPublic] = useState(plan.isPublic);
  const [limits, setLimits] = useState<Record<string, string>>(
    Object.fromEntries(plan.limits.map((l) => [l.limitKey, l.value === null ? '' : String(l.value)])),
  );
  const [features, setFeatures] = useState<Record<string, boolean>>(
    Object.fromEntries(plan.features.map((f) => [f.featureKey, f.enabled])),
  );

  const save = useMutation({
    mutationFn: () =>
      adminApi.updatePlan(plan.id, {
        name: name.trim(),
        active,
        isPublic,
        // An empty box means unlimited, which the API models as null.
        limits: Object.fromEntries(
          Object.entries(limits).map(([key, value]) => [key, value.trim() === '' ? null : Number(value)]),
        ),
        features,
      }),
    onSuccess: () => {
      toast.success(`${name} updated`);
      void queryClient.invalidateQueries({ queryKey: adminKeys.plans() });
      onClose();
    },
    onError: (error) => toast.error(messageForError(error)),
  });

  const limitsValid = Object.values(limits).every(
    (value) => value.trim() === '' || (Number.isInteger(Number(value)) && Number(value) >= 0),
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {plan.name}</DialogTitle>
          <DialogDescription>
            Applies to new and existing licences on this plan. Leave a limit blank for unlimited.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="plan-name">Name</Label>
              <Input id="plan-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="flex gap-6 text-data">
              <label className="flex items-center gap-2">
                <Switch checked={active} onCheckedChange={setActive} aria-label="Active" /> Active
              </label>
              <label className="flex items-center gap-2">
                <Switch checked={isPublic} onCheckedChange={setIsPublic} aria-label="Public" /> Public
              </label>
            </div>
            <fieldset className="flex flex-col gap-2">
              <legend className="section-label">Limits</legend>
              {Object.entries(limits).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <Label htmlFor={`limit-${key}`} className="font-mono text-micro">
                    {key}
                  </Label>
                  <Input
                    id={`limit-${key}`}
                    className="w-28"
                    inputMode="numeric"
                    placeholder="∞"
                    value={value}
                    onChange={(e) => setLimits((prev) => ({ ...prev, [key]: e.target.value }))}
                  />
                </div>
              ))}
            </fieldset>
            <fieldset className="flex flex-col gap-2">
              <legend className="section-label">Features</legend>
              {Object.entries(features).map(([key, enabled]) => (
                <label key={key} className="flex items-center justify-between gap-4 text-data">
                  <span className="font-mono text-micro">{key}</span>
                  <Switch
                    checked={enabled}
                    aria-label={key}
                    onCheckedChange={(next) => setFeatures((prev) => ({ ...prev, [key]: next }))}
                  />
                </label>
              ))}
            </fieldset>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || !limitsValid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CouponForm() {
  const [code, setCode] = useState('');
  const [percent, setPercent] = useState('25');
  const [duration, setDuration] = useState<'once' | 'repeating' | 'forever'>('once');
  const create = useMutation({
    mutationFn: () =>
      adminApi.createCoupon({ code: code.trim(), type: 'percent', percentOff: Number(percent), duration }),
    onSuccess: () => {
      toast.success(`Coupon ${code.trim().toUpperCase()} created`);
      setCode('');
    },
    onError: (error) => toast.error(messageForError(error)),
  });
  const valid = code.trim().length >= 3 && Number(percent) >= 1 && Number(percent) <= 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Discount code</CardTitle>
        <CardDescription>Percentage discounts. Codes are stored uppercase.</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) create.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="coupon-code">Code</Label>
            <Input id="coupon-code" value={code} onChange={(e) => setCode(e.target.value)} className="w-44" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="coupon-percent">Percent off</Label>
            <Input id="coupon-percent" inputMode="numeric" value={percent} onChange={(e) => setPercent(e.target.value)} className="w-24" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="coupon-duration">Duration</Label>
            <select
              id="coupon-duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value as typeof duration)}
              className="h-9 rounded-md border border-hairline bg-elevated px-3 text-data"
            >
              <option value="once">Once</option>
              <option value="repeating">Repeating</option>
              <option value="forever">Forever</option>
            </select>
          </div>
          <Button type="submit" disabled={!valid || create.isPending}>
            Create code
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function CataloguePage() {
  const query = useQuery(adminPlansQuery());
  const [editing, setEditing] = useState<AdminPlan | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Platform"
        title="Plan catalogue"
        description="Plans, limits, features and prices live in the database, so commercial changes need no deployment."
      />

      {query.isPending ? <Skeleton className="h-64 w-full" /> : null}
      {query.isError ? <p className="text-data text-danger">{messageForError(query.error)}</p> : null}

      <div className="grid gap-6 lg:grid-cols-2">
        {query.data?.items.map((plan) => (
          <Card key={plan.id}>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>{plan.name}</CardTitle>
                <div className="flex items-center gap-2">
                  <StatusChip tone={plan.active ? 'active' : 'neutral'}>{plan.active ? 'Active' : 'Inactive'}</StatusChip>
                  {plan.isCustom ? <StatusChip tone="info" showDot={false}>Custom</StatusChip> : null}
                </div>
              </div>
              <CardDescription>
                <span className="font-mono">{plan.key}</span>
                {plan.description ? ` · ${plan.description}` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-data">
                {plan.limits.map((limit) => (
                  <div key={limit.limitKey} className="flex justify-between">
                    <dt className="font-mono text-micro text-ink-subtle">{limit.limitKey}</dt>
                    <dd data-numeric>{limit.value ?? '∞'}</dd>
                  </div>
                ))}
              </dl>
              <ul className="flex flex-col divide-y divide-hairline">
                {plan.prices.map((price) => (
                  <li key={price.id} className="flex justify-between py-1.5 text-data">
                    <span className="text-ink-muted">
                      {price.kind === 'addon_site' ? 'Extra site' : 'Base'} · {price.interval}
                    </span>
                    <span data-numeric>{formatMoney(price.unitAmount, price.currency)}</span>
                  </li>
                ))}
                {plan.prices.length === 0 ? (
                  <li className="py-1.5 text-data text-ink-muted">No prices (trial or custom quote).</li>
                ) : null}
              </ul>
              <Button variant="secondary" size="sm" className="self-start" onClick={() => setEditing(plan)}>
                Edit plan
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <CouponForm />

      {editing ? <PlanEditor plan={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/catalogue')({
  component: CataloguePage,
});
